// src/belediye/pages/ApplicationDetailsPage.js

import React, { useEffect, useMemo, useState } from "react";
import BasicModal from "../components/ui/BasicModal";
import { downloadFile } from "../components/utils/downloadFile";

import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";

import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchApplicationDetails,
  deleteApplication,
  fetchForwardLogsByApplication,
} from "../redux/slices/belediyeApplicationDetailsSlice";

import TrDateTime from "../components/utils/TrDateTime";
import {
  safeText,
  ensureFirstUpperTr,
  titleCaseTr,
} from "../components/utils/textFormat";

import ApplicationActionPanel from "../components/ApplicationActionPanel";
import HalkMasasiNoteCard from "../components/applications/HalkMasasiNoteCard";
import useReadReceipt from "../hooks/useReadReceipt";
// FAZ-105 — resmî belge (PDF) çıktısı
import usePermissions from "../hooks/usePermissions";
import { acResmiBelgeOnizleme } from "../services/belediyeApi";
// FAZ-105 — yeni yeteneği fark ettiren tanıtım balonu
import useYeniOzellikIpucu from "../hooks/useYeniOzellikIpucu";
import YeniOzellikBalonu from "../components/ui/YeniOzellikBalonu";
// FAZ-88: detay başlığını navbara bildir (dar katmanda orta yuvaya yazılır)
import { useNavbarBasligiAyarla } from "../components/NavbarBaslikContext";

// FAZ-29: başvuru↔görev köprüsü — "İlişkili Görevler" kartı (uiOn + modül açıkken;
// kendi kapısını taşır, kapalıyken hiç render olmaz)
import IliskiliGorevlerKarti from "../components/tasks/IliskiliGorevlerKarti";

import "./ApplicationDetailsPage.css";
import "./ApplicationDetailsPage.mobile.css";

// Heroicons
import {
  UserIcon,
  DocumentTextIcon,
  PaperClipIcon,
  MapPinIcon,
  ChevronUpDownIcon,
  WrenchIcon,
  ArrowPathRoundedSquareIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";

// Leaflet icon ayarı
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const locationIcon = new L.Icon({
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const pick = (...vals) =>
  vals.find((v) => v !== null && v !== undefined && v !== "");

// Yönlendiren kişi adı: sadece ad-soyad/full_name; username’i göstermeyiz.
const formatForwardByName = (log) => {
  const u = log?.created_by || {};

  const first = pick(u?.first_name, log?.created_by_first_name);
  const last = pick(u?.last_name, log?.created_by_last_name);

  const fullFromParts = [first, last].filter(Boolean).join(" ").trim();
  if (fullFromParts) return titleCaseTr(fullFromParts);

  const full = pick(
    u?.full_name,
    log?.created_by_full_name,
    log?.created_byFullName
  );
  if (full && String(full).trim()) return titleCaseTr(String(full).trim());

  const maybeName = pick(log?.created_by_name, "");
  if (maybeName && /\s/.test(String(maybeName).trim())) {
    return titleCaseTr(String(maybeName).trim());
  }

  return "Bilinmiyor";
};

const ApplicationDetailsPage = () => {
  const { id, unitSlug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    applicationData,
    unitDetails,
    neighborhoodDetails,
    loading,
    error,
    forwardLogs,
    forwardLogsLoading,
    forwardLogsError,
  } = useSelector((state) => state.belediyeApplicationDetails);

  /* FAZ-88 — dar katmanda navbarın ORTA yuvası bu kaydı yazar, SOL yuvası
     geri okuna döner. Navbar rotayı bilir ama kaydı bilmez; köprü budur. */
  useNavbarBasligiAyarla(
    applicationData
      ? {
          ad: applicationData.subject || "Başvuru",
          no: applicationData.tracking_number || (id ? `#${id}` : ""),
        }
      : null
  );

  const token = localStorage.getItem("accessToken");
  const userRaw = localStorage.getItem("user");

  const user = useMemo(() => {
    try {
      return userRaw ? JSON.parse(userRaw) : null;
    } catch {
      return null;
    }
  }, [userRaw]);

  const isAuthenticated = Boolean(token && user);

  const forwardLogItems = useMemo(() => {
    if (Array.isArray(forwardLogs)) return forwardLogs;
    if (Array.isArray(forwardLogs?.results)) return forwardLogs.results;
    return [];
  }, [forwardLogs]);

  const forwardLogsForThisApp = useMemo(() => {
    return forwardLogItems.filter((l) => {
      const appId =
        l?.application_detail?.id ??
        l?.application_detail?.pk ??
        l?.application ??
        l?.application_id;

      return String(appId) === String(id);
    });
  }, [forwardLogItems, id]);

  const forwardLogsOrdered = useMemo(() => {
    return [...forwardLogsForThisApp].sort((a, b) => {
      const aDate = a?.created_at || a?.created || a?.createdAt || null;
      const bDate = b?.created_at || b?.created || b?.createdAt || null;

      const aTime = aDate ? new Date(aDate).getTime() : null;
      const bTime = bDate ? new Date(bDate).getTime() : null;

      if (aTime !== null && bTime !== null) return aTime - bTime;

      const aId = Number(a?.id ?? 0);
      const bId = Number(b?.id ?? 0);
      return aId - bId;
    });
  }, [forwardLogsForThisApp]);

  /* ------------------------------------------------------------------
     FAZ-105 — RESMÎ BELGE ÇIKTISI

     Kapı SADECE can() — bilerek `uiOn && can()` DEĞİL:
       • computeUiOn, izin dizisinin DOLU olmasını şart koşuyor. Superuser'ın
         dizisi boş gelebildiği için `uiOn && can()` düğmeyi SUPERUSER'DAN DA
         gizliyordu (testle yakalandı).
       • can() zaten güvenli varsayılana sahip: superuser → true, izinliye →
         true, izin dizisi boşsa herkese → false.
     Diğer yüzeylerdeki `uiOn ? can(x) : eskiRolKontrolü` deseni burada
     geçerli değil: bu yepyeni bir yetenek, eski rol dünyasında karşılığı yok.
     Nihai kapı her hâlükârda backend'de (applications.document.print).
  ------------------------------------------------------------------ */
  const { can } = usePermissions();
  const belgeAlinabilir = can("applications.document.print");
  const [belgeYukleniyor, setBelgeYukleniyor] = useState(false);

  /* "YENİ" tanıtım balonu — izni yeni açılan kişi düğmeyi fark etsin.
     Yalnız düğme GERÇEKTEN çiziliyken sayılır: izinsiz kullanıcıda hak
     yanmaz. İlk 3 detay ziyaretinde çıkar, sonra kendiliğinden susar. */
  const yeniIpucu = useYeniOzellikIpucu("resmiBelgeYazdir", {
    aktif: belgeAlinabilir,
    adet: 3,
  });

  const handleResmiBelge = async () => {
    if (belgeYukleniyor) return;   // çift tıklama iki üretim tetiklemesin
    // Özelliği kullanan kişinin tanıtıma ihtiyacı kalmadı.
    yeniIpucu.kapat(true);
    setBelgeYukleniyor(true);

    // Sekmeyi İSTEKTEN ÖNCE aç: tarayıcılar, kullanıcı tıklamasından uzun süre
    // sonra açılan pencereyi açılır-pencere engelleyiciye takar. Önce boş sekme
    // açılır, içerik gelince oraya yazılır.
    const sekme = window.open("", "_blank");

    try {
      const { blob } = await acResmiBelgeOnizleme(id);
      const url = URL.createObjectURL(blob);
      if (sekme) {
        sekme.location.href = url;
      } else {
        // Engelleyici sekmeyi kapattıysa aynı pencerede aç
        window.location.href = url;
      }
      // Sekme yüklendikten sonra blob URL'i serbest bırak
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (sekme) sekme.close();
      const durum = err?.response?.status;
      if (durum === 403) {
        alert("Bu başvurunun belge çıktısını alma yetkiniz bulunmuyor.");
      } else {
        alert("Belge oluşturulamadı. Lütfen tekrar deneyin.");
      }
    } finally {
      setBelgeYukleniyor(false);
    }
  };

  const [mediaModal, setMediaModal] = useState({
    open: false,
    url: "",
    type: "image",
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const [collapsedCards, setCollapsedCards] = useState({
    contact: false,
    details: false,
    attachments: false,
    map: false,
    hmNote: false,
    forwardHistory: false,
  });

  const toggleCard = (key) => {
    setCollapsedCards((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // FAZ-54 — "okundu" kaydı: detayda 5 saniyeden fazla kalınırsa. Veri yüklenene
  // kadar (loading/hata) sayaç başlamaz; boş ekranda beklemek okuma değildir.
  // Hook koşulsuz çağrılır (React kuralı) — kapıyı `ready` bayrağı tutar.
  useReadReceipt(id, {
    ready: Boolean(isAuthenticated && applicationData && !loading && !error),
  });

  /* ------------------------------------------------------------------
     VERİ ÇEKME
  ------------------------------------------------------------------ */
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/giris");
      return;
    }

    if (!id) return;

    dispatch(fetchApplicationDetails({ id }));
  }, [dispatch, id, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !id) return;
    dispatch(fetchForwardLogsByApplication({ applicationId: id }));
  }, [dispatch, id, isAuthenticated]);

  /* ------------------------------------------------------------------
     MODAL HANDLERS
  ------------------------------------------------------------------ */
  const openMedia = (url, type) => setMediaModal({ open: true, url, type });
  const closeMedia = () => setMediaModal({ open: false, url: "", type: "image" });

  /* ------------------------------------------------------------------
     SİLME
  ------------------------------------------------------------------ */
  const handleDelete = () => {
    dispatch(deleteApplication({ id }))
      .then((res) => {
        if (!res.error) {
          alert("Başvuru silindi.");
          navigate("/yonetim/basvurular"); // FAZ-22 4b: canonical liste dönüşü
        } else {
          alert("Başvuru silinemedi.");
        }
      })
      .catch(() => {
        alert("Başvuru silinemedi.");
      });
  };

  /* ------------------------------------------------------------------
     DURUM KONTROL
  ------------------------------------------------------------------ */
  if (loading) return <p>Yükleniyor...</p>;
  if (error) return <p>Hata: {error}</p>;
  if (!applicationData) return <p>Başvuru bulunamadı.</p>;

  const {
    citizen,
    subject,
    description,
    files,
    latitude,
    longitude,
    forwarded_note,
    forwarded_from_unit_name,
    tracking_number,
    address,
    documents,
  } = applicationData;

  const citizenFullName = titleCaseTr(
    [
      pick(citizen?.name, citizen?.first_name),
      pick(citizen?.surname, citizen?.last_name),
    ]
      .filter(Boolean)
      .join(" ")
  );

  const citizenPhone = safeText(
    pick(citizen?.phone_number, citizen?.phone, citizen?.gsm, citizen?.mobile)
  );

  const createdAtRaw = pick(
    applicationData?.created_at,
    applicationData?.createdAt,
    applicationData?.created,
    applicationData?.created_date,
    applicationData?.createdDate,
    applicationData?.status_date,
    applicationData?.statusDate
  );

  return (
    <div className="details-layout-isolated">
      <div className="details-wrapper">
        <div className="details-grid">
          {/* ---------------------------------------------------------
             SOL KOLON
          --------------------------------------------------------- */}
          <div className="left-column">
            {/* CONTACT */}
            <div
              className={`details-card details-card--kv ${
                collapsedCards.contact ? "details-card--collapsed" : ""
              }`}
            >
              <div
                className="card-header card-header--clickable"
                onClick={() => toggleCard("contact")}
              >
                <div className="card-header-left">
                  <UserIcon className="card-header-icon" />
                  <span>İletişim Bilgileri</span>
                </div>
                <ChevronUpDownIcon
                  className={
                    collapsedCards.contact
                      ? "card-header-toggle-icon collapsed"
                      : "card-header-toggle-icon"
                  }
                />
              </div>

              {!collapsedCards.contact && (
                <div className="card-body">
                  <p>
                    <strong>Başvuru ID:</strong>{" "}
                    {applicationData?.id ?? applicationData?.pk ?? id}
                  </p>
                  <p>
                    <strong>İsim Soyisim:</strong> {citizenFullName}
                  </p>
                  <p>
                    <strong>Telefon:</strong> {citizenPhone}
                  </p>

                  {citizen?.email && (
                    <p>
                      <strong>E-posta:</strong> {citizen.email}
                    </p>
                  )}

                  {unitDetails && (
                    <p>
                      <strong>İlgili Birim:</strong> {unitDetails.name}
                    </p>
                  )}

                  <p>
                    <strong>Takip No:</strong> {tracking_number}
                  </p>

                  {forwarded_from_unit_name && (
                    <>
                      <p>
                        <strong>Yönlendiren Birim:</strong>{" "}
                        {forwarded_from_unit_name}
                      </p>
                      {forwarded_note && (
                        <p>
                          <strong>Not:</strong>{" "}
                          {ensureFirstUpperTr(forwarded_note)}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* DETAILS */}
            <div
              className={`details-card details-card--kv ${
                collapsedCards.details ? "details-card--collapsed" : ""
              }`}
            >
              <div
                className="card-header card-header--clickable"
                onClick={() => toggleCard("details")}
              >
                <div className="card-header-left">
                  <DocumentTextIcon className="card-header-icon" />
                  <span>Başvuru Detayları</span>
                </div>
                <ChevronUpDownIcon
                  className={
                    collapsedCards.details
                      ? "card-header-toggle-icon collapsed"
                      : "card-header-toggle-icon"
                  }
                />
              </div>

              {!collapsedCards.details && (
                <div className="card-body">
                  <p>
                    <strong>Başvuru Tarihi:</strong>{" "}
                    <TrDateTime value={createdAtRaw} />
                  </p>

                  <p>
                    <strong>Konu:</strong>{" "}
                    <span className="details-value">
                      {ensureFirstUpperTr(subject)}
                    </span>
                  </p>

                  <p>
                    <strong>Açıklama:</strong>{" "}
                    <span className="details-value">
                      {ensureFirstUpperTr(description)}
                    </span>
                  </p>

                  {address && (
                    <p>
                      <strong>Adres:</strong>{" "}
                      <span className="details-value">
                        {ensureFirstUpperTr(address)}
                      </span>
                    </p>
                  )}

                  {neighborhoodDetails && (
                    <p>
                      <strong>Mahalle:</strong> {neighborhoodDetails.name}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ATTACHMENTS */}
            {(files?.length > 0 || documents?.length > 0) && (
              <div
                className={`details-card ${
                  collapsedCards.attachments ? "details-card--collapsed" : ""
                }`}
              >
                <div
                  className="card-header card-header--clickable"
                  onClick={() => toggleCard("attachments")}
                >
                  <div className="card-header-left">
                    <PaperClipIcon className="card-header-icon" />
                    <span>Ek Dosyalar</span>
                  </div>
                  <ChevronUpDownIcon
                    className={
                      collapsedCards.attachments
                        ? "card-header-toggle-icon collapsed"
                        : "card-header-toggle-icon"
                    }
                  />
                </div>

                {!collapsedCards.attachments && (
                  <div className="card-body">
                    {files?.length > 0 && (
                      <div className="file-grid">
                        {files.map((fileUrl, i) => (
                          <div key={i} className="file-grid-item">
                            <img
                              src={fileUrl}
                              className="file-image"
                              alt=""
                              onClick={() => openMedia(fileUrl, "image")}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {documents?.length > 0 && (
                      <div className="document-grid">
                        {documents.map((docUrl, i) => (
                          <div
                            key={i}
                            className="document-card"
                            onClick={() => openMedia(docUrl, "pdf")}
                          >
                            <div className="document-icon">📄</div>
                            <div className="document-name">Belge {i + 1}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* MAP */}
            {latitude && longitude && (
              <div
                className={`details-card ${
                  collapsedCards.map ? "details-card--collapsed" : ""
                }`}
              >
                <div
                  className="card-header card-header--clickable"
                  onClick={() => toggleCard("map")}
                >
                  <div className="card-header-left">
                    <MapPinIcon className="card-header-icon" />
                    <span>Konum Bilgisi</span>
                  </div>
                  <ChevronUpDownIcon
                    className={
                      collapsedCards.map
                        ? "card-header-toggle-icon collapsed"
                        : "card-header-toggle-icon"
                    }
                  />
                </div>

                {!collapsedCards.map && (
                  <div className="card-body">
                    <MapContainer
                      center={[latitude, longitude]}
                      zoom={13}
                      style={{
                        height: "240px",
                        width: "100%",
                      }}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <Marker
                        position={[latitude, longitude]}
                        icon={locationIcon}
                      />
                    </MapContainer>
                  </div>
                )}
              </div>
            )}

            {/* HM NOTE — FAZ-53: veri artık detail-bundle'dan (assisted_intake).
                Eskiden halk-masasi/feed ucundan geliyordu ve o uç HM rolüyle
                korunduğu için müdür/birim personeli notu HİÇ göremiyordu. */}
            <HalkMasasiNoteCard
              assistedIntake={applicationData?.assisted_intake}
              collapsed={collapsedCards.hmNote}
              onToggle={() => toggleCard("hmNote")}
            />

            {/* FORWARD HISTORY */}
            {!forwardLogsLoading &&
              !forwardLogsError &&
              forwardLogsForThisApp.length > 0 && (
                <div
                  className={`details-card ${
                    collapsedCards.forwardHistory ? "details-card--collapsed" : ""
                  }`}
                >
                  <div
                    className="card-header card-header--clickable"
                    onClick={() => toggleCard("forwardHistory")}
                  >
                    <div className="card-header-left">
                      <ArrowPathRoundedSquareIcon className="card-header-icon" />
                      <span>Yönlendirme Geçmişi</span>
                    </div>
                    <ChevronUpDownIcon
                      className={
                        collapsedCards.forwardHistory
                          ? "card-header-toggle-icon collapsed"
                          : "card-header-toggle-icon"
                      }
                    />
                  </div>

                  {!collapsedCards.forwardHistory && (
                    <div className="card-body">
                      <div className="forward-history">
                        {forwardLogsOrdered.map((log, idx) => {
                          const by = formatForwardByName(log);

                          const oldUnit =
                            log.old_unit_detail?.name ||
                            log.old_unit_name ||
                            log.old_unit?.name ||
                            log.old_unit ||
                            "—";

                          const newUnit =
                            log.new_unit_detail?.name ||
                            log.new_unit_name ||
                            log.new_unit?.name ||
                            log.new_unit ||
                            "—";

                          const note = log.note || "";
                          const createdAt =
                            log.created_at || log.created || log.createdAt || null;

                          const orderNo = idx + 1;
                          const isLast = idx === forwardLogsOrdered.length - 1;

                          return (
                            <div key={log.id || idx} className="forward-item">
                              <div className="forward-item-header">
                                <div className="forward-by">
                                  <span className="forward-step">{orderNo}</span>
                                  <span className="forward-by-text">{by}</span>

                                  {isLast && (
                                    <span className="forward-badge forward-badge--last">
                                      Son yönlendirme
                                    </span>
                                  )}
                                </div>

                                <div className="forward-at">
                                  {createdAt ? <TrDateTime value={createdAt} /> : "—"}
                                </div>
                              </div>

                              <div className="forward-units">
                                <strong>{oldUnit}</strong> → <strong>{newUnit}</strong>
                              </div>

                              {note && (
                                <div className="forward-note">
                                  <div className="forward-note-label">Not</div>
                                  <div className="forward-note-text">
                                    {ensureFirstUpperTr(note)}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* FAZ-29 — İlişkili Görevler (Yönlendirme Geçmişi'ne KARIŞTIRILMAZ;
                o kart ForwardLog'a özel). İki katmanlı KVKK görünürlüğü backend'de. */}
            <IliskiliGorevlerKarti
              applicationId={applicationData?.id}
              trackingNumber={applicationData?.tracking_number}
              subject={applicationData?.subject}
            />
          </div>

          {/* ---------------------------------------------------------
             SAĞ KOLON
          --------------------------------------------------------- */}
          <div className="right-column">
            <div className="details-card actions-card">
              <div className="card-header">
                <div className="card-header-left">
                  <WrenchIcon className="card-header-icon" />
                  <span>İşlemler</span>
                </div>

                {/* FAZ-105 — RESMÎ BELGE ÇIKTISI
                    İşlemler kartının başlık şeridinde, sağ köşede durur
                    (kullanıcı isteği). Yazdırmak da bir işlemdir; kartların
                    üstünde ayrı bir şerit olarak durduğunda hem yer harcıyor
                    hem hangi eyleme ait olduğu belirsiz kalıyordu. */}
                {belgeAlinabilir && (
                  <YeniOzellikBalonu
                    gorunur={yeniIpucu.gorunur}
                    baslik="Belge çıktısı"
                  >
                    <button
                      type="button"
                      className="belge-btn belge-btn--baslik"
                      onClick={handleResmiBelge}
                      disabled={belgeYukleniyor}
                      title="Başvurunun tüm bilgilerini antetli belge biçiminde açar; yazdırabilir veya PDF olarak kaydedebilirsiniz"
                    >
                      <PrinterIcon className="belge-btn-ikon" />
                      <span>
                        {belgeYukleniyor ? "Hazırlanıyor…" : "Yazdır"}
                      </span>
                    </button>
                  </YeniOzellikBalonu>
                )}
              </div>

              <div className="card-body">
                <ApplicationActionPanel
                  applicationId={id}
                  unitSlug={unitSlug}
                  onDelete={handleDelete}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      <BasicModal
        open={mediaModal.open}
        title={mediaModal.type === "pdf" ? "Belge" : "Görsel"}
        onClose={closeMedia}
        actions={
          mediaModal.url ? (
            <>
              <a
                className="btn-md btn-md-ghost"
                href={mediaModal.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Yeni sekmede aç
              </a>

              <button
                type="button"
                className="btn-md btn-md-primary"
                onClick={() =>
                  downloadFile(mediaModal.url, {
                    token: localStorage.getItem("accessToken"),
                    filename:
                      mediaModal.type === "pdf" ? "belge.pdf" : "gorsel.jpg",
                  })
                }
              >
                <ArrowDownTrayIcon width={18} height={18} />
                İndir
              </button>
            </>
          ) : null
        }
      >
        {mediaModal.type === "pdf" ? (
          <iframe
            title="PDF"
            src={mediaModal.url}
            className="md-modal-iframe"
          />
        ) : (
          <img
            src={mediaModal.url}
            alt="Büyük görsel"
            className="md-modal-image"
          />
        )}
      </BasicModal>
    </div>
  );
};

export default ApplicationDetailsPage;